import { IContent } from 'models/content.model';
import React from 'react';
import globalContent from 'static/globalContent/';

export interface ITranslationVariables {
  [key: string]: string;
}

interface IContentUtilityOptions {
  content?: IContent;
  componentName: string;
}

export const createDataTestId = (title: string | undefined, prefix = ''): string => {
  // replace space, coma+space, space+&+space with -
  const regex = /( & *)|(, *)|( )/g;

  return `${prefix}${(title || '').toLowerCase().replace(regex, '-')}-card`;
};

class ContentUtil {
  private content: IContent = { nl: {}, en: {} };

  readonly componentName: string;

  constructor({ content = globalContent, componentName = 'global' }: IContentUtilityOptions) {
    this.componentName = componentName;
    if (this.componentName === 'global' && !content) {
      this.content = globalContent;
    } else {
      this.content = content;
    }
  }

  /**
   * Displays simple text, optionally with replaced variables. HTML is literally displayed.
   * @param key translation key
   * @param variables flat object of variables
   */
  translate(key: string, variables?: ITranslationVariables): React.ReactNode | string {
    const str = this.translateToString(key, variables);
    if (process.env.NODE_ENV === 'development') {
      return <span title={`${this.componentName} > ${key}`}>{str}</span>;
    }
    if (!this.hasTranslation(key)) return '';
    return <span>{str}</span>;
  }

  /**
   * Renders HTML, optionally with replaced variables
   * @param key translation key
   * @param variables flat object of variables
   */
  translateHtml(key: string, variables?: ITranslationVariables, defaultKey?: string): React.ReactNode {
    const str = this.translateToString(key, variables);
    if (defaultKey && str === `[${key}]`) {
      return this.translateHtml(defaultKey, variables);
    }

    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line react/no-danger
      return <span title={`${this.componentName} > ${key}`} dangerouslySetInnerHTML={{ __html: str }} />;
    }
    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: str }} />;
  }

  /**
   * Returns a string, optionally with replaced variables
   * @param key translation key
   * @param variables flat object of variables
   */
  translateToString(key: string, variables?: ITranslationVariables): string {
    let str: any = this.content.nl;

    const segments = key?.split('.') || [];

    segments.forEach((segment) => {
      str = str?.[segment];
    });

    if (typeof str !== 'string') {
      return `[${key}]`;
    }

    if (variables) {
      Object.keys(variables).forEach((variableKey) => {
        str = ((str as string) || '').replace(new RegExp(`#{${variableKey}}`, 'g'), variables[variableKey]);
      });
    }

    return str;
  }

  hasTranslation(key: string): boolean {
    let value: any = this.content.nl;
    const segments = key?.split('.') || [];

    const found = segments.every((segment) => {
      if (value && typeof value === 'object' && segment in value) {
        value = value[segment];
        return true;
      }
      return false;
    });

    if (!found) return false;

    return value !== undefined && value !== null;
  }
}

const globalContentUtil = new ContentUtil({ componentName: 'global' });

export { ContentUtil, globalContentUtil };
